import { useState, useEffect, useMemo } from "react";
import { Table, TableBody, TableBodyRow, TableHeaderItemProps } from "components/table";
import { Input } from "components";
import { SearchIconTwo } from "assets";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useFetchBillingHistory, CreditTypeFilter, useExportBillingHistory } from "hooks";
import { PaginationTwo } from "components";

const PAGE_SIZE = 8;

const CURRENCY_SYMBOLS: Record<string, string> = {
  NGN: "₦",
  USD: "$",
  GBP: "£",
  EUR: "€",
};

interface BillingRecord {
  id: string;
  transactionId: string;
  date: string;
  creditType: string;
  amount: string;
  status: "Success" | "Pending" | "Failed";
}

const ALL_CREDIT_TYPES = "All types";
const CREDIT_TYPE_OPTIONS = [ALL_CREDIT_TYPES, "Free", "Purchased"];

/**
 * Records predating the credit system carry a plan name ("Scale", "Growth")
 * where the credit type now goes. Every one of those was a paid transaction, so
 * anything that isn't explicitly free reads as purchased.
 */
const toCreditType = (value: string): string =>
  /^free$/i.test(value?.trim() ?? "") ? "Free" : "Purchased";

const searchSchema = Yup.object().shape({
  search: Yup.string().trim(),
});

const BillingHistoryTable = () => {
  const [selectedCreditType, setSelectedCreditType] = useState(ALL_CREDIT_TYPES);
  const [selectedSort, setSelectedSort] = useState("latest");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [enableExport, setEnableExport] = useState(false);

  // Fetch billing history with filters
  const creditTypeFilter: CreditTypeFilter | undefined =
    selectedCreditType === ALL_CREDIT_TYPES
      ? undefined
      : (selectedCreditType.toLowerCase() as CreditTypeFilter);
  const { data: billingData, isLoading } = useFetchBillingHistory({
    creditType: creditTypeFilter,
    sort: selectedSort as any,
    search: searchQuery,
    page: currentPage,
  });

  const totalCount = billingData?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Export hook
  const { data: exportData, isLoading: isExporting } = useExportBillingHistory(enableExport, selectedRows);

  useEffect(() => {
    if (exportData && !isExporting) {
      // Download CSV
      const blob = new Blob([exportData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing-history-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setEnableExport(false);
    }
  }, [exportData, isExporting]);

  const { register, formState: { errors }, watch } = useForm({
    resolver: yupResolver(searchSchema),
    mode: "onBlur",
    defaultValues: {
      search: "",
    },
  });
  const watchSearch = watch("search");

  const sortOptions = [
    { label: "Most Recent", value: "latest" },
    { label: "Oldest First", value: "oldest" },
    { label: "amount (High)", value: "amount_desc" },
    { label: "amount (Low)", value: "amount_asc" },
  ];

  // Transform API data to table format
  const tableData: BillingRecord[] = useMemo(() => {
    if (!billingData?.results) return [];

    return billingData.results.map((record) => ({
      id: record.id.toString(),
      transactionId: record.reference,
      date: record.date,
      creditType: toCreditType(record.plan),
      amount: `${CURRENCY_SYMBOLS[record.currency] || record.currency}${record.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      status: record.status as any,
    }));
  }, [billingData]);

  // Watch for search input changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(watchSearch?.trim() || "");
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [watchSearch]);

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const toggleAllRows = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((row) => row.id));
    }
  };

  // The server already applies `creditTypeFilter`; this only guards against a
  // stale page of results arriving after the filter changed.
  const filteredData = tableData.filter(
    (record) =>
      selectedCreditType === ALL_CREDIT_TYPES ||
      record.creditType?.toLowerCase() === selectedCreditType.toLowerCase()
  );

  // Custom checkbox header element
  const CheckboxHeader = (
    <input
      type="checkbox"
      checked={selectedRows.length === filteredData.length && filteredData.length > 0}
      onChange={toggleAllRows}
      className="w-5 h-5 rounded-md border-gray-300 cursor-pointer accent-[#012169]"
    />
  );

  const tableHeaders: TableHeaderItemProps[] = [
    { title: CheckboxHeader, styles: { width: "50px", flexShrink: 0 } },
    { title: "Transaction ID", styles: { flex: 1 } },
    { title: "Date", styles: { flex: 1 } },
    { title: "Credit Type", styles: { flex: 1 } },
    { title: "Amount", styles: { flex: 1 } },
    { title: "Status", styles: { flex: 1 } },
  ];

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-12">
        <div className="flex flex-wrap gap-3 items-center">
            {/* Credit Type Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsTypeDropdownOpen(!isTypeDropdownOpen);
                  setIsSortDropdownOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:border-gray-300"
              >
                {selectedCreditType}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isTypeDropdownOpen && (
                <div className="absolute left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {CREDIT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedCreditType(option);
                        setCurrentPage(1);
                        setIsTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${selectedCreditType === option ? "text-[#012169] font-medium" : "text-gray-600"
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsSortDropdownOpen(!isSortDropdownOpen);
                  setIsTypeDropdownOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:border-gray-300"
              >
                {sortOptions.find(option => option.value === selectedSort)?.label || "Sort"}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isSortDropdownOpen && (
                <div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedSort(option.value);
                        setCurrentPage(1);
                        setIsSortDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${selectedSort === option.value ? "text-[#012169] font-medium" : "text-gray-600"
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <Input
              Icon={SearchIconTwo}
              iconClassName="!top-[11px] !left-[14px]"
              placeholder="Search bills"
              type="search"
              name="search"
              register={register}
              validatorMessage={errors.search?.message || ""}
              showRequired={false}
              parentClassName="!mb-0 !w-fit"
              className="!h-[42px] !rounded-lg !border-gray-200 hover:!border-gray-300 !pl-10 !text-sm !text-gray-600 placeholder:!text-gray-400 !w-fit min-w-[200px]"
            />

        </div>

        {/* Export Button */}
        <button 
          onClick={() => setEnableExport(true)}
          disabled={isExporting || selectedRows.length === 0}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#012169] text-white text-sm font-medium rounded-lg hover:bg-[#011557] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? "Exporting..." : "Export CSV"}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table
          tableHeaderTitles={tableHeaders}
          customTableClasses={{
            tableContainerClassName: "min-w-[700px]",
            tableHeaderClassName: "bg-transparent border-b border-gray-100 !shadow-none !justify-start !gap-0",
            tableHeaderItemClassName: "!text-[#1D2939] text-sm font-medium !p-0",
          }}
          tableBody={
            <TableBody customClassName="mt-0">
              {filteredData.map((record) => (
                <TableBodyRow
                  key={record.id}
                  customClassName="!shadow-none border-b border-gray-50 hover:bg-gray-50 !rounded-none !mb-0 !justify-start !gap-0"
                >
                  <span style={{ width: "50px", flexShrink: 0 }} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(record.id)}
                      onChange={() => toggleRowSelection(record.id)}
                      className="w-5 h-5 rounded-md border-gray-300 cursor-pointer accent-[#012169]"
                    />
                  </span>
                  <span style={{ flex: 1 }} className="text-sm text-gray-600">{record.transactionId}</span>
                  <span style={{ flex: 1 }} className="text-sm text-gray-600">{record.date}</span>
                  <span style={{ flex: 1 }} className="text-sm font-medium text-gray-900 capitalize">{record.creditType}</span>
                  <span style={{ flex: 1 }} className="text-sm font-medium text-gray-900">{record.amount}</span>
                  <span style={{ flex: 1 }}>
                    <span
                      className={`text-sm font-medium px-3 py-1.5 rounded-full ${record.status === "Success"
                        ? "text-[#069952] bg-[#E6FFF3]/70"
                        : record.status === "Pending"
                          ? "text-yellow-600 bg-yellow-50"
                          : "text-red-600 bg-red-50"
                        }`}
                    >
                      {record.status}
                    </span>
                  </span>
                </TableBodyRow>
              ))}
            </TableBody>
          }
          emptyTable={{
            show: isLoading || filteredData.length === 0,
            element: isLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading billing history...</p>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                No billing records found
              </div>
            ),
          }}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTwo
          current={currentPage}
          total={totalPages}
          handleChange={setCurrentPage}
          count={totalCount}
          length={PAGE_SIZE}
        />
      )}
    </div>
  );
};

export { BillingHistoryTable };
